import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Grid,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Badge,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  Flag as FlagIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { moderationService } from '../services/moderationService';

interface FlaggedItem {
  flag_id: string;
  content_id: string;
  content_type: string;
  reason: string;
  priority: 'high' | 'medium' | 'low';
  status: string;
  created_at: string;
  content_data?: any;
  additional_data?: any;
}

interface ModerationQueue {
  high_priority: FlaggedItem[];
  medium_priority: FlaggedItem[];
  low_priority: FlaggedItem[];
  total_count: number;
  summary: any;
}

const ModerationQueue: React.FC = () => {
  const [queue, setQueue] = useState<ModerationQueue | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [moderationDialog, setModerationDialog] = useState<{
    open: boolean;
    item: FlaggedItem | null;
  }>({ open: false, item: null });
  const [moderationAction, setModerationAction] = useState('');
  const [moderationReason, setModerationReason] = useState('');
  const [moderationNotes, setModerationNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadModerationQueue();
  }, []);

  const loadModerationQueue = async () => {
    try {
      setLoading(true);
      const data = await moderationService.getModerationQueue();
      setQueue(data);
      setError(null);
    } catch (err) {
      setError('Failed to load moderation queue');
      console.error('Error loading moderation queue:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleModerate = async () => {
    if (!moderationDialog.item || !moderationAction || !moderationReason) {
      return;
    }

    try {
      setProcessing(true);
      
      if (moderationDialog.item.content_type === 'review') {
        await moderationService.moderateReview(
          moderationDialog.item.content_id,
          {
            action: moderationAction as any,
            reason: moderationReason,
            notes: moderationNotes,
            flag_id: moderationDialog.item.flag_id,
          }
        );
      } else if (moderationDialog.item.content_type === 'location_submission') {
        await moderationService.moderateLocationSubmission(
          moderationDialog.item.content_id,
          {
            action: moderationAction as any,
            reason: moderationReason,
            notes: moderationNotes,
            flag_id: moderationDialog.item.flag_id,
          }
        );
      }

      // Refresh queue
      await loadModerationQueue();
      
      // Close dialog
      setModerationDialog({ open: false, item: null });
      setModerationAction('');
      setModerationReason('');
      setModerationNotes('');
      
    } catch (err) {
      setError('Failed to moderate content');
      console.error('Error moderating content:', err);
    } finally {
      setProcessing(false);
    }
  };

  const openModerationDialog = (item: FlaggedItem) => {
    setModerationDialog({ open: true, item });
  };

  const closeModerationDialog = () => {
    setModerationDialog({ open: false, item: null });
    setModerationAction('');
    setModerationReason('');
    setModerationNotes('');
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'high':
        return <ErrorIcon color="error" />;
      case 'medium':
        return <WarningIcon color="warning" />;
      case 'low':
        return <InfoIcon color="info" />;
      default:
        return <FlagIcon />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  const renderFlaggedItem = (item: FlaggedItem) => (
    <Card key={item.flag_id} sx={{ mb: 2 }}>
      <CardContent>
        <Box display="flex" alignItems="center" mb={2}>
          {getPriorityIcon(item.priority)}
          <Typography variant="h6" sx={{ ml: 1, flexGrow: 1 }}>
            {item.content_type === 'review' ? 'Review' : 'Location Submission'}
          </Typography>
          <Chip
            label={item.priority}
            color={getPriorityColor(item.priority) as any}
            size="small"
          />
        </Box>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          <strong>Reason:</strong> {item.reason}
        </Typography>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          <strong>Flagged:</strong> {new Date(item.created_at).toLocaleString()}
        </Typography>

        {item.content_data && (
          <Box mt={2}>
            <Typography variant="subtitle2" gutterBottom>
              Content Preview:
            </Typography>
            
            {item.content_type === 'review' && (
              <Box>
                <Typography variant="body2">
                  <strong>Rating:</strong> {item.content_data.rating}/5 stars
                </Typography>
                <Typography variant="body2">
                  <strong>Text:</strong> {item.content_data.text || 'No text'}
                </Typography>
                <Typography variant="body2">
                  <strong>User:</strong> {item.content_data.user_id}
                </Typography>
              </Box>
            )}

            {item.content_type === 'location_submission' && (
              <Box>
                <Typography variant="body2">
                  <strong>Name:</strong> {item.content_data.name}
                </Typography>
                <Typography variant="body2">
                  <strong>Description:</strong> {item.content_data.description || 'No description'}
                </Typography>
                <Typography variant="body2">
                  <strong>Submitted by:</strong> {item.content_data.submitted_by}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {item.additional_data && (
          <Accordion sx={{ mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">
                Moderation Analysis
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <pre style={{ fontSize: '12px', overflow: 'auto' }}>
                {JSON.stringify(item.additional_data, null, 2)}
              </pre>
            </AccordionDetails>
          </Accordion>
        )}
      </CardContent>

      <CardActions>
        <Button
          startIcon={<VisibilityIcon />}
          onClick={() => openModerationDialog(item)}
        >
          Review
        </Button>
      </CardActions>
    </Card>
  );

  const getTabLabel = (label: string, count: number) => (
    <Badge badgeContent={count} color="primary">
      {label}
    </Badge>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
        <Button onClick={loadModerationQueue} sx={{ ml: 2 }}>
          Retry
        </Button>
      </Alert>
    );
  }

  if (!queue) {
    return null;
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Content Moderation Queue
      </Typography>

      {queue.total_count === 0 ? (
        <Alert severity="success">
          No content flagged for moderation. Great job!
        </Alert>
      ) : (
        <>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={selectedTab} onChange={(_, newValue) => setSelectedTab(newValue)}>
              <Tab label={getTabLabel('High Priority', queue.high_priority.length)} />
              <Tab label={getTabLabel('Medium Priority', queue.medium_priority.length)} />
              <Tab label={getTabLabel('Low Priority', queue.low_priority.length)} />
            </Tabs>
          </Box>

          <Box>
            {selectedTab === 0 && queue.high_priority.map(renderFlaggedItem)}
            {selectedTab === 1 && queue.medium_priority.map(renderFlaggedItem)}
            {selectedTab === 2 && queue.low_priority.map(renderFlaggedItem)}
          </Box>
        </>
      )}

      {/* Moderation Dialog */}
      <Dialog
        open={moderationDialog.open}
        onClose={closeModerationDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Moderate {moderationDialog.item?.content_type === 'review' ? 'Review' : 'Location Submission'}
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Action</InputLabel>
              <Select
                value={moderationAction}
                onChange={(e) => setModerationAction(e.target.value)}
                label="Action"
              >
                {moderationDialog.item?.content_type === 'review' ? (
                  <>
                    <MenuItem value="approve">Approve</MenuItem>
                    <MenuItem value="remove">Remove</MenuItem>
                    <MenuItem value="flag">Flag for Further Review</MenuItem>
                    <MenuItem value="warn_user">Warn User</MenuItem>
                    <MenuItem value="suspend_user">Suspend User</MenuItem>
                  </>
                ) : (
                  <>
                    <MenuItem value="approve">Approve</MenuItem>
                    <MenuItem value="reject">Reject</MenuItem>
                    <MenuItem value="request_revision">Request Revision</MenuItem>
                  </>
                )}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Reason"
              value={moderationReason}
              onChange={(e) => setModerationReason(e.target.value)}
              required
              sx={{ mb: 2 }}
              multiline
              rows={2}
            />

            <TextField
              fullWidth
              label="Additional Notes (Optional)"
              value={moderationNotes}
              onChange={(e) => setModerationNotes(e.target.value)}
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeModerationDialog}>
            Cancel
          </Button>
          <Button
            onClick={handleModerate}
            variant="contained"
            disabled={!moderationAction || !moderationReason || processing}
            startIcon={processing ? <CircularProgress size={20} /> : <CheckIcon />}
          >
            {processing ? 'Processing...' : 'Apply Action'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ModerationQueue;